import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MaterialService } from "@root/services/material";
import { useNotification } from "./useNotification";
import { PAGINATION_DEFAULTS } from "@root/constants";

export function useMaterials() {
  const [materialsByDepartmentData, setMaterialsByDepartmentData] =
    useState<any>([]);
  const [materialCount, setMaterialCount] = useState<number>(0);
  const [materialHasMore, setMaterialHasMore] = useState<boolean>(true);
  const [addMaterialsByDepartmentLoading, setAddMaterialsByDepartmentLoading] =
    useState(false);
  const [
    updateMaterialsByDepartmentLoading,
    setUpdateMaterialsByDepartmentLoading,
  ] = useState(false);
  const [
    deleteMaterialsByDepartmentLoading,
    setDeleteMaterialsByDepartmentLoading,
  ] = useState(false);
  const [getMaterialsByDepartmentLoading, setGetMaterialsByDepartmentLoading] =
    useState(false);

  const materialService = new MaterialService();
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");

  const fetchMaterialsByDepartment = async (payload: {
    id: number;
    searchQuery?: string;
    offset?: number;
    limit?: number;
    append?: boolean;
  }) => {
    const {
      id,
      searchQuery = "",
      offset = 0,
      limit = PAGINATION_DEFAULTS.PAGESIZE,
      append = false,
    } = payload;

    setGetMaterialsByDepartmentLoading(true);
    try {
      const response = await materialService.fetchMaterialsByDepartment(id, {
        offset,
        limit,
        searchQuery,
      });

      if (response?.data?.data) {
        const newRows = response.data.data.rows || [];
        const totalCount = response.data.data.count || 0;

        setMaterialCount(totalCount);

        if (append) {
          // Deduplicate data by id
          setMaterialsByDepartmentData((prev: any[]) => {
            const existingIds = new Set(prev.map((item: any) => item.id));
            const uniqueNewRows = newRows.filter(
              (item: any) => !existingIds.has(item.id)
            );
            return [...prev, ...uniqueNewRows];
          });
        } else {
          setMaterialsByDepartmentData(newRows);
        }

        // Check if there's more data to load
        setMaterialHasMore(offset + newRows.length < totalCount);

        return response?.data;
      }
    } catch (error: any) {
      showNotification({
        message: t("assessments.notifications.fetchMaterialsError"),
        type: "error",
      });
      throw error;
    } finally {
      setGetMaterialsByDepartmentLoading(false);
    }
  };

  return {
    materialsByDepartmentData,
    setMaterialsByDepartmentData,
    materialCount,
    materialHasMore,
    getMaterialsByDepartmentLoading,
    fetchMaterialsByDepartment,
  };
}
